import { SipRecommendationActionType } from '../../../../api/common.types'
import type { DerivedIdealSip } from '../../../deriveSipPlansView'
import { showAsFormattedRupeeAmount } from '../../../../../display-values/lumpsumPlanAmount'
import {
  formatTargetSip,
  formatTargetSipLabel
} from '../../../../../display-values/sipPlanAmount'
import { useOpenSipPlanRecommendationBottomSheet } from '../../../../plan-bottom-sheet/open/useOpenSipPlanRecommendationBottomSheet'
import { useSipPlansContext } from '../../../sipPlansContext'
import { IdealAllocationPieIcon } from '../../../../../lumpsum/plans-list/lumpsum-ui-components/PlansCategoryRangeTrailing'
import {
  PlansFundCard,
  type PlanFundCardCta
} from '../../../../../lumpsum/plans-list/lumpsum-ui-components/PlansFundCard'
import { usePlanSipStartAction } from '../../../../../execution/sip/start/usePlanSipStartAction'
import { useIncreaseSipAction } from '../../../../../execution/sip/increase/usePlanIncreaseSipAction'
import { useDecreaseSipAction } from '../../../../../execution/sip/decrease/usePlanDecreaseSipAction'
import { SipAmountBadge } from '../../yourPlan/cards/sipYourPlanFundCardShared'
import { buildPlanAllocationRangeLabel } from '../../../../../display-values/lumpsumPlanPercentage'
import { PlansFundRole, PlansScreen } from '../../../../../analytics/plansEvents'
import { trackPlansFundCardCta, trackPlansFundCardOpen } from '../../../../../analytics/plansTrack'

type IdealSipFundCardProps = {
  fund: DerivedIdealSip
  showBottomDivider?: boolean
}

export function IdealSipFundCard({ fund, showBottomDivider = true }: IdealSipFundCardProps) {
  const { plansOverview } = useSipPlansContext()
  const { openIdealSipPlanRecommendationBottomSheet } = useOpenSipPlanRecommendationBottomSheet()
  const { onStartSip } = usePlanSipStartAction()
  const { onIncreaseSip } = useIncreaseSipAction()
  const { onDecreaseSip } = useDecreaseSipAction()
  const totalIdealSipAmount = plansOverview?.totalIdealSipAmount ?? 0
  const isin = fund?.isin ?? ''
  const fundName = fund?.fundName ?? ''
  const screenName = PlansScreen.SipIdeal
  const fundRole = PlansFundRole.Ideal
  const currentSipAmount = fund?.currentSipAmount ?? 0
  const recommendedSipAmount = fund?.recommendedSipAmount ?? 0
  const actionType = 'actionType' in fund ? fund.actionType : undefined
  const idealAllocationRangeLabel = buildPlanAllocationRangeLabel({ idealAllocation: fund?.idealAllocationPercentage })
  const recommendedSipAmountLabel =
    recommendedSipAmount > 0
      ? `${formatTargetSipLabel({ rawAmount: recommendedSipAmount })}/mo`
      : ''
  const formattedTargetSip = formatTargetSip({ rawAmount: recommendedSipAmount })
  const formattedCurrentSip = showAsFormattedRupeeAmount({ rawAmount: currentSipAmount })

  const openPlanBottomSheet = () => {
    openIdealSipPlanRecommendationBottomSheet({ isin, totalIdealSipAmount })
  }

  const handleCardClick = () => {
    trackPlansFundCardOpen({ screenName, fundRole, fundName, fundIsin: isin })
    openPlanBottomSheet()
  }

  const ctas: PlanFundCardCta[] = []
  if (actionType === SipRecommendationActionType.START) {
    ctas.push({
      id: `${isin}-start-sip`,
      label: 'Start SIP',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Start SIP', fundName, fundIsin: isin })
        onStartSip({
          isin,
          fundName,
          initiateSipCta: plansOverview?.initiateSipCta,
          amount: formattedTargetSip.value
        })
      },
      variant: 'tertiary'
    })
  } else if (actionType === SipRecommendationActionType.INCREASE) {
    ctas.push({
      id: `${isin}-edit-sip`,
      label: 'Edit SIP',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Edit SIP', fundName, fundIsin: isin })
        onIncreaseSip(fund, plansOverview?.initiateSipCta)
      },
      variant: 'tertiary'
    })
  } else if (actionType === SipRecommendationActionType.DECREASE) {
    ctas.push({
      id: `${isin}-edit-sip`,
      label: 'Edit SIP',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Edit SIP', fundName, fundIsin: isin })
        onDecreaseSip(fund, plansOverview?.manageSipCta)
      },
      variant: 'tertiary'
    })
  } else {
    ctas.push({
      id: `${isin}-no-change`,
      label: 'No action',
      onClick: () => {
        trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'No action', fundName, fundIsin: isin })
      },
      variant: 'completed'
    })
  }
  ctas.push({
    id: `${isin}-learn-more`,
    label: 'Learn more',
    onClick: () => {
      trackPlansFundCardCta({ screenName, fundRole, buttonLabel: 'Learn more', fundName, fundIsin: isin })
      openPlanBottomSheet()
    },
    variant: 'tertiary'
  })

  return (
    <PlansFundCard
      fund={{
        isin,
        logoUrl: fund?.mfLogoUrl ?? '',
        title: fundName,
        fundNature: fund?.fundNature ?? '',
        category: fund?.fundCategory ?? '',
        purchaseMode: fund?.purchaseMode ?? ''
      }}
      topRight={
        <div className="text-right shrink-0 mt-[2px]">
          {recommendedSipAmountLabel.length > 0 ? (
            <div className="text-[13px] font-[500] text-[#232323] -mt-[3px]">
              {recommendedSipAmountLabel}
            </div>
          ) : null}
          {idealAllocationRangeLabel.length > 0 ? (
            <div className={`${recommendedSipAmount > 0 ? 'pt-0.5 ' : ''}flex justify-end`}>
              <div className="flex shrink-0 items-center gap-1 rounded-[6px] bg-[#F0F4F7] pt-[1px] pb-[2px] pl-1.5 pr-1 text-[12px] font-[500] text-[#3D3D3D] mt-[2px]">
                <IdealAllocationPieIcon size={10} />
                {idealAllocationRangeLabel}
              </div>
            </div>
          ) : null}
        </div>
      }
      badgeRowExtras={currentSipAmount > 0 ? <SipAmountBadge currentSipAmount={formattedCurrentSip} /> : null}
      status={{
        rank: fund?.powerRank ?? 0,
        rating: fund?.powerRating ?? ''
      }}
      ctas={ctas}
      showBottomDivider={showBottomDivider}
      onCardClick={handleCardClick}
    />
  )
}
